import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircleIcon, XIcon, CheckIcon } from 'lucide-react';
import { FloatingConfig, FloatingDevice } from '../../types';

/**
 * Elementos flotantes opcionales de una landing: botón de WhatsApp y avisos de
 * actividad.
 *
 * Reglas de rendimiento (deliberadas):
 *  - Si un elemento está desactivado NO se monta nada: ni temporizadores, ni
 *    listeners, ni consultas. El componente devuelve `null` antes de crearlos.
 *  - Sin widgets externos, sin iframes y sin scripts de terceros: WhatsApp es
 *    un enlace `wa.me` normal.
 *  - Las animaciones usan solo `opacity` y `transform`, y respetan
 *    `prefers-reduced-motion` a través de las clases globales.
 */

type Viewport = 'desktop' | 'tablet' | 'mobile';
interface FloatingElementsProps {
  config?: FloatingConfig;
  /**
   * Dispositivo simulado en la vista previa del constructor. En la landing
   * pública se deja sin definir y se mide el ancho real de la ventana: antes
   * siempre valía «desktop», así que un elemento configurado solo para móvil
   * no aparecía nunca en el teléfono.
   */
  viewport?: Viewport;
}

/** Dispositivo real de la ventana, con los mismos cortes que el CSS. */
function useRealViewport(): Viewport {
  const [value, setValue] = useState<Viewport>(() => {
    if (typeof window === 'undefined') return 'desktop';
    const width = window.innerWidth;
    return width <= 640 ? 'mobile' : width <= 1024 ? 'tablet' : 'desktop';
  });
  useEffect(() => {
    function measure(): void {
      const width = window.innerWidth;
      setValue(width <= 640 ? 'mobile' : width <= 1024 ? 'tablet' : 'desktop');
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return value;
}

/**
 * Los elementos flotantes son secundarios: se montan cuando el navegador está
 * libre (o al segundo, como tope), de modo que no compiten con el logo, el
 * titular, la miniatura del vídeo ni el CTA en la primera pintura. En la vista
 * previa del constructor se montan al instante para que editar sea inmediato.
 */
function useDeferredMount(immediate: boolean): boolean {
  const [ready, setReady] = useState(immediate);
  useEffect(() => {
    if (immediate) {
      setReady(true);
      return;
    }
    const idle = (window as any).requestIdleCallback as ((cb: () => void, options?: {
      timeout: number;
    }) => number) | undefined;
    if (idle) {
      const handle = idle(() => setReady(true), {
        timeout: 1000
      });
      return () => (window as any).cancelIdleCallback?.(handle);
    }
    const timer = window.setTimeout(() => setReady(true), 600);
    return () => window.clearTimeout(timer);
  }, [immediate]);
  return ready;
}

/** ¿Debe verse este elemento en el dispositivo actual? */
function visibleOn(devices: FloatingDevice[] | undefined, viewport: string): boolean {
  const list = devices && devices.length > 0 ? devices : ['desktop', 'tablet', 'mobile'];
  return list.includes(viewport as FloatingDevice);
}
function corner(position: string | undefined): string {
  return position === 'izquierda' ? 'left-4' : 'right-4';
}
export function FloatingElements({
  config,
  viewport
}: FloatingElementsProps) {
  const real = useRealViewport();
  const ready = useDeferredMount(viewport !== undefined);
  const device = viewport ?? real;
  if (!config || !ready) return null;
  return <>
      <WhatsappBubble config={config} viewport={device} data-id="element-546" data-mp="1w3t65qzxe3m5" />
      <ActivityToasts config={config} viewport={device} data-id="element-547" data-mp="1r1k9ajjxmher" />
    </>;
}
function WhatsappBubble({
  config,
  viewport
}: {
  config: FloatingConfig;
  viewport: string;
}) {
  const whatsapp = config.whatsapp;
  const [open, setOpen] = useState(false);
  // Sin activar no se monta absolutamente nada.
  if (!whatsapp?.enabled) return null;
  if (!visibleOn(whatsapp.devices, viewport)) return null;
  const digits = (whatsapp.phone ?? '').replace(/\D/g, '');
  if (!digits) return null;
  const href = `https://wa.me/${digits}${whatsapp.message ? `?text=${encodeURIComponent(whatsapp.message)}` : ''}`;
  const size = Math.min(Math.max(whatsapp.size ?? 48, 40), 72);
  const side = corner(whatsapp.position);
  return <div className={`fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] ${side} z-[60] flex flex-col items-end gap-2 print:hidden`} data-id="element-548" data-mp="h5x91n831hy9">
      {open && whatsapp.showCard !== false && <div role="dialog" aria-label="Contacto por WhatsApp" className="ava-fade-in max-w-[240px] rounded-2xl border border-border bg-background p-3 shadow-lg" data-id="element-549" data-mp="3km42l0ws9ytb">
          <button type="button" aria-label="Cerrar" onClick={() => setOpen(false)} className="float-right -mr-1 -mt-1 rounded-md p-1 text-muted-foreground hover:bg-surface" data-id="element-550" data-mp="3pi2yraocsl4a">
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-551" data-mp="365pz82plbde" />
          </button>
          <p className="pr-4 text-sm font-medium text-navy" data-id="element-552" data-mp="31u1sy9ik7isk">
            {whatsapp.cardTitle || '¿Tienes alguna duda?'}
          </p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground" data-id="element-553" data-mp="36rl4v2rsbjhc">
            {whatsapp.cardText || 'Escríbeme por WhatsApp y te respondo enseguida.'}
          </p>
          <a href={href} target="_blank" rel="noreferrer" className="mt-3 flex h-10 items-center justify-center rounded-xl bg-[#25D366] px-4 text-sm font-semibold text-white" data-id="element-554" data-mp="2ubv7qgfzr8fq">
            Abrir WhatsApp
          </a>
        </div>}
      {whatsapp.showCard === false ? <a href={href} target="_blank" rel="noreferrer" aria-label="Escríbenos por WhatsApp" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white shadow-lg transition-transform active:scale-95" style={{
      height: size,
      minWidth: size,
      paddingInline: whatsapp.label ? 16 : 0
    }} data-id="element-555" data-mp="2gc2nn3yyzqgq">
          <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-556" data-mp="21o7slgxhpa0h" />
          {whatsapp.label && <span className="text-sm font-semibold" data-id="element-557" data-mp="1wma1k0iulawz">{whatsapp.label}</span>}
        </a> : <button type="button" aria-expanded={open} aria-label="Escríbenos por WhatsApp" onClick={() => setOpen(v => !v)} className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white shadow-lg transition-transform active:scale-95" style={{
      height: size,
      minWidth: size,
      paddingInline: whatsapp.label ? 16 : 0
    }} data-id="element-558" data-mp="1slq4ewy9y7au">
          <MessageCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-559" data-mp="192j4u8jn0jyg" />
          {whatsapp.label && <span className="text-sm font-semibold" data-id="element-560" data-mp="1e1dmv89s64hi">{whatsapp.label}</span>}
        </button>}
    </div>;
}
function ActivityToasts({
  config,
  viewport
}: {
  config: FloatingConfig;
  viewport: string;
}) {
  const activity = config.activity;
  const enabled = Boolean(activity?.enabled) && visibleOn(activity?.devices, viewport);
  const messages = useMemo(() => (activity?.messages ?? []).filter(m => (m.title ?? '').trim() || (m.text ?? '').trim()), [activity?.messages]);
  /*
   * Rotación natural: el orden se baraja una vez por visita (Fisher–Yates), en
   * lugar de recorrer siempre la lista desde el primer aviso. Antes cada carga
   * empezaba por el mismo mensaje, así que con dos o tres apariciones por
   * visita el visitante veía Madrid una y otra vez. Al recorrer una permutación
   * de forma secuencial tampoco puede repetirse la misma ciudad dos veces
   * seguidas. Es cálculo local: ni consultas, ni peticiones, ni polling.
   */
  const order = useMemo(() => {
    const list = messages.map((_, i) => i);
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }, [messages]);
  const [index, setIndex] = useState(-1);
  const shown = useRef(0);
  useEffect(() => {
    // Los temporizadores solo existen si el elemento está activado.
    if (!enabled || messages.length === 0) return;
    const delay = Math.max(activity?.delay ?? 6, 1) * 1000;
    const duration = Math.max(activity?.duration ?? 6, 2) * 1000;
    const interval = Math.max(activity?.interval ?? 18, 4) * 1000;
    const max = Math.max(activity?.max ?? 3, 1);
    let hideTimer: number | undefined;
    let nextTimer: number | undefined;
    function show(): void {
      if (shown.current >= max) return;
      setIndex(order[shown.current % order.length]);
      shown.current += 1;
      hideTimer = window.setTimeout(() => {
        setIndex(-1);
        nextTimer = window.setTimeout(show, interval);
      }, duration);
    }
    const first = window.setTimeout(show, delay);
    return () => {
      window.clearTimeout(first);
      if (hideTimer) window.clearTimeout(hideTimer);
      if (nextTimer) window.clearTimeout(nextTimer);
    };
  }, [enabled, messages, order, activity?.delay, activity?.duration, activity?.interval, activity?.max]);
  if (!enabled || index < 0 || !messages[index]) return null;
  const message = messages[index];
  const side = corner(activity?.position);
  const bottom = config.whatsapp?.enabled ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))]' : 'bottom-[calc(1rem+env(safe-area-inset-bottom))]';
  return <div role="status" aria-live="polite" className={`ava-fade-in fixed ${bottom} ${side} z-[59] flex max-w-[260px] items-start gap-2.5 rounded-2xl border border-border bg-background p-3 shadow-lg print:hidden`} data-id="element-561" data-mp="pd8enczocue6">
      <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-50 text-emerald-600" data-id="element-562" data-mp="fmx06wid72t1">
        <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-563" data-mp="al7qbk6vov6j" />
      </span>
      <span className="min-w-0" data-id="element-564" data-mp="18aj4y83bwdhh">
        <span className="block text-[13px] font-semibold leading-tight text-navy" data-id="element-565" data-mp="13bye2z9yv65p">
          {message.title}
        </span>
        {message.text && <span className="mt-0.5 block text-xs leading-snug text-muted-foreground" data-id="element-566" data-mp="ybtvn3pi01cu">
            {message.text}
          </span>}
        {activity?.showTime !== false && message.time && <span className="mt-1 block text-[11px] text-muted-foreground" data-id="element-567" data-mp="3j3lw7okbmu4g">
            {message.time}
          </span>}
      </span>
      <button type="button" aria-label="Cerrar aviso" onClick={() => setIndex(-1)} className="-mr-1 -mt-1 flex-none rounded-md p-1 text-muted-foreground hover:bg-surface" data-id="element-568" data-mp="3f0pkmht77ya0">
        <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-569" data-mp="1av82llbignk1" />
      </button>
    </div>;
}